import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getChartOfAccountById, createChartOfAccount, updateChartOfAccount, getChartOfAccounts } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import FloatingInput from "../components/UI/FloatingInput";
import SelectBox from "../components/UI/SelectBox";
import ToggleSwitch from "../components/UI/ToggleSwitch";
import { notify } from "../utils/notify";
import PageHeader from "../components/UI/PageHeader";
import EntityTranslationsButton from "../components/UI/EntityTranslationsButton";
import { useTranslation } from "../context/I18nContext";

const ACCOUNT_TYPES = ["asset", "liability", "equity", "revenue", "expense"];

export default function AddEditAccount() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("accounting");

  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({ code: "", name: "", type: "asset", parent_id: "", is_active: true });
  const [systemKey, setSystemKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    getChartOfAccounts(token).then(setAccounts).catch(() => notify.error(t("notify_load_accounts_failed", "Failed to load accounts")));

    if (id) {
      getChartOfAccountById(token, id).then((acc) => {
        setForm({
          code: acc.code || "",
          name: acc.name || "",
          type: acc.type || "asset",
          parent_id: acc.parent_id ? String(acc.parent_id) : "",
          is_active: acc.is_active !== undefined ? acc.is_active : true,
        });
        setSystemKey(acc.system_key || "");
      }).catch(() => notify.error(t("notify_load_account_failed", "Failed to load account")));
    }
  }, [id, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const typeOptions = ACCOUNT_TYPES.map((tp) => ({ value: tp, label: t(`account_type_${tp}`, tp) }));

  // An account can't be its own parent — filter it out once we know its id.
  const parentOptions = [
    { value: "", label: t("parent_none_option", "No parent (top level)") },
    ...accounts.filter((a) => String(a.id) !== id).map((a) => ({ value: String(a.id), label: `${a.code} — ${a.name}` })),
  ];

  const setField = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = async () => {
    setErrors({});
    const newErrors = {};
    if (!form.code.trim()) newErrors.code = t("validation_code_required", "Account code is required");
    if (!form.name.trim()) newErrors.name = t("validation_account_name_required", "Account name is required");

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      notify.error(tCommon("validation_fill_required_fields", "Please fill in the required fields correctly"));
      return;
    }

    setLoading(true);
    const payload = {
      code: form.code.trim(),
      name: form.name.trim(),
      type: form.type,
      parent_id: form.parent_id ? Number(form.parent_id) : 0,
      is_active: form.is_active,
    };

    try {
      if (id) {
        await updateChartOfAccount(token, id, payload);
        notify.success(t("notify_updated", "Account updated successfully"));
      } else {
        await createChartOfAccount(token, payload);
        notify.success(t("notify_account_created", "Account created successfully"));
      }
      navigate("/accounts");
    } catch (err) {
      notify.error(err.data?.error || err.message || tCommon("error_generic", "Something went wrong"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto">
      <PageHeader className="flex items-center gap-4 mb-8 border-b border-gray-100 pb-6">
        <button onClick={() => navigate("/accounts")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
          <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
        </button>
        <div>
          <h2 className="text-2xl font-black text-gray-800 tracking-tight">{id ? t("edit_title", "Edit Account") : t("add_title", "Add New Account")}</h2>
          <p className="text-gray-400 text-sm italic font-medium">{t("form_subtitle", "Every account the accounting system can post a debit or credit into")}</p>
        </div>
      </PageHeader>

      <div className="bg-white p-6 md:p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
        <div className="space-y-6">
          {systemKey && (
            <div className="p-4 rounded-2xl bg-amber-50 text-amber-700 text-sm font-medium">
              {t("system_account_notice", "This is a system account the accounting engine posts to automatically — its Type is locked, but the Code, Name, Parent, and Active status stay fully editable.")}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FloatingInput label={t("field_code_label", "Account Code")} value={form.code} onChange={setField("code")} error={errors.code} icon={ICONS.ChartOfAccounts} />
            <div className="flex items-end gap-3">
              <div className="flex-grow">
                <FloatingInput label={t("field_name_label", "Account Name")} value={form.name} onChange={setField("name")} error={errors.name} />
              </div>
              <EntityTranslationsButton entityType="account" entityId={id} defaultName={form.name} />
            </div>
          </div>

          <SelectBox
            label={t("field_type_label", "Account Type")}
            options={typeOptions}
            value={form.type}
            onChange={(val) => setForm((prev) => ({ ...prev, type: val }))}
            isDisabled={!!systemKey}
          />

          <SelectBox
            label={t("field_parent_label", "Parent Account (optional)")}
            options={parentOptions}
            value={form.parent_id}
            onChange={(val) => setForm((prev) => ({ ...prev, parent_id: val }))}
          />

          <ToggleSwitch
            label={tCommon("status_active", "Active")}
            checked={form.is_active}
            onChange={(val) => setForm((prev) => ({ ...prev, is_active: val }))}
          />

          <div className="flex gap-4 pt-6 border-t border-gray-50">
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={loading}
              className="flex-grow shadow-lg shadow-blue-500/10"
              icon={id ? ICONS.Save : ICONS.Plus}
            >
              {id ? t("btn_update_account", "Update Account") : t("btn_create_account", "Create Account")}
            </Button>
            <Button variant="secondary" onClick={() => navigate("/accounts")}>{tCommon("btn_cancel", "Cancel")}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
